import React, { useState, useEffect } from "react";
import AdminLayout from "./Admin/AdminLayout";
import AdminSettings, {
  MathAccessSettings,
  TheologyAccessSettings,
  SpanishExamPasswordSettings,
} from "./Admin/AdminSettings";
import OperationPasswordSettings from "./Admin/OperationPasswordSettings";
import CreateProject from "./Projects/CreateProject";
import ProjectList from "./Projects/ProjectList";
import ProjectDetail from "./Projects/ProjectDetail";
import { MathCoursesManager, MathLibraryEditor } from "./Admin/MathContentManager";
import "./Admin/admin.css";

export default function AdminDashboard({
  user,
  onNewProject,
  projects,
  onDeleteProject,
  onUpdateProject,
}) {
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [localProjects, setLocalProjects] = useState(projects || []);
  const [viewMode, setViewMode] = useState("overview");

  useEffect(() => {
    setLocalProjects(projects || []);
  }, [projects]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("admin-session-expires-at");
    window.location.href = "/login";
  };

  if (!user?.isAdmin) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          color: "var(--admin-primary)",
          fontFamily: "Inter, sans-serif",
          fontSize: 16,
        }}
      >
        You are not authorized to view this page.
      </div>
    );
  }

  return (
    <AdminLayout
      user={user}
      onLogout={handleLogout}
      viewMode={viewMode}
      setViewMode={(mode) => {
        setViewMode(mode);
        if (mode !== "projectDetail") setSelectedProjectId(null);
      }}
      projects={localProjects}
    >
      {/* ── Project List ── */}
      {viewMode === "projectList" && (
        <div className="admin-content-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">Project List</h2>
            <p className="admin-section-sub">
              {localProjects.length} project{localProjects.length !== 1 ? "s" : ""} total
            </p>
          </div>
          <ProjectList
            projects={localProjects}
            onDeleteProject={onDeleteProject}
            onSelectProject={(id) => {
              setSelectedProjectId(id);
              setViewMode("projectDetail");
            }}
            onSelectDraft={(draft) => {
              setSelectedDraft(draft);
              setViewMode("project");
            }}
          />
        </div>
      )}

      {/* ── Create / Edit Project ── */}
      {viewMode === "project" && (
        <div className="admin-content-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">
              {selectedDraft ? "Edit Draft Project" : "Create New Project"}
            </h2>
            <p className="admin-section-sub">
              Fill in the details below and save.
            </p>
          </div>
          <CreateProject
            onNewProject={(p) => {
              onNewProject(p);
              setSelectedDraft(null);
            }}
            draftData={selectedDraft || null}
          />
        </div>
      )}

      {/* ── Project Detail / Edit ── */}
      {viewMode === "projectDetail" && selectedProjectId && (
        <div className="admin-content-section">
          <ProjectDetail
            project={localProjects?.find((p) => p._id === selectedProjectId)}
            editable={true}
            onUpdateProject={(updated) => {
              setLocalProjects((prev) =>
                prev.map((p) => (p._id === updated._id ? updated : p))
              );
              if (onUpdateProject) onUpdateProject(updated);
              setSelectedProjectId(null);
              setViewMode("projectList");
            }}
            onBack={() => {
              setSelectedProjectId(null);
              setViewMode("projectList");
            }}
          />
        </div>
      )}

      {/* ── Settings & Study Submodules ── */}
      {viewMode === "settings" && <AdminSettings />}
      {viewMode === "operationPassword" && <OperationPasswordSettings />}
      {viewMode === "csDataSciencePassword" && <OperationPasswordSettings />}
      {viewMode === "mathLibrary" && <MathLibraryEditor />}
      {viewMode === "mathCourses" && <MathCoursesManager />}
      {viewMode === "mathAccess" && <MathAccessSettings />}
      {viewMode === "theologyAccess" && <TheologyAccessSettings />}
      {viewMode === "spanishExamPassword" && <SpanishExamPasswordSettings />}
    </AdminLayout>
  );
}
